import { supabase } from "../supabase";
import { emailApi } from "./email";
import { notificationsApi } from "./notifications";
import { getPairInvitationTemplate } from "@/lib/utils/email-templates";

/**
 * Pairs API helpers using Supabase
 */

export interface PairInvitation {
  id: number;
  inviter_id: string;
  invitee_email: string;
  invitee_user_id: string | null;
  status: "pending" | "accepted" | "declined" | "expired";
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Pair {
  id: number;
  user_a_id: string;
  user_b_id: string | null;
  user_b_placeholder: string | null;
  status: "active" | "inactive";
  created_at: string;
  updated_at: string;
}

export const pairsApi = {
  /**
   * Fetch all pairs for current user
   */
  async list(): Promise<{ success: boolean; data?: Pair[]; error?: string }> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("pairs")
        .select("*")
        .or(`user_a_id.eq.${user.id},user_b_id.eq.${user.id}`)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return { success: true, data: data as Pair[] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch pairs",
      };
    }
  },

  /**
   * Create a new pair.
   * Ak userBId je null, userBPlaceholder je povinný (dočasný pár).
   */
  async create(
    userAId: string,
    userBId: string | null,
    userBPlaceholder?: string,
  ): Promise<{ success: boolean; data?: Pair; error?: string }> {
    try {
      const pairData: {
        user_a_id: string;
        user_b_id: string | null;
        user_b_placeholder?: string | null;
        status: string;
      } = {
        user_a_id: userAId,
        user_b_id: userBId,
        status: "active",
      };
      if (userBId == null) {
        pairData.user_b_placeholder =
          (userBPlaceholder?.trim() || null) ?? null;
      }

      const { data, error } = await supabase
        .from("pairs")
        .insert(pairData)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data: data as Pair };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to create pair",
      };
    }
  },

  /**
   * Aktualizovať pár – prepojiť user_b_id keď partner sa zaregistruje
   */
  async updatePair(
    pairId: number,
    updates: { user_b_id?: string | null; user_b_placeholder?: string | null },
  ): Promise<{ success: boolean; data?: Pair; error?: string }> {
    try {
      const { data, error } = await supabase
        .from("pairs")
        .update(updates)
        .eq("id", pairId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data: data as Pair };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Nepodarilo sa aktualizovať pár",
      };
    }
  },

  /**
   * Pozvať používateľa emailom na tanečný pár.
   * Vytvorí pozvánku, odošle email a notifikáciu. Pár sa vytvorí až po prijatí.
   */
  async inviteByEmail(inviteeEmail: string): Promise<{
    success: boolean;
    data?: PairInvitation;
    error?: string;
  }> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: profile } = await supabase
        .from("users")
        .select("role")
        .eq("id", user.id)
        .single();
      if (profile?.role !== "dancer") {
        throw new Error("Pozvávať partnera môžu len tanečníci");
      }

      const trimmedEmail = inviteeEmail.trim().toLowerCase();
      if (!trimmedEmail) throw new Error("Zadajte email adresu");

      if (trimmedEmail === user.email) {
        throw new Error("Nemôžete pozvať samého seba");
      }

      // Nájsť používateľa s týmto emailom (musí byť approved)
      const { data: inviteeUser, error: userError } = await supabase
        .from("users")
        .select("id, email, display_name")
        .ilike("email", trimmedEmail)
        .eq("status", "approved")
        .maybeSingle();

      if (userError) throw userError;
      if (!inviteeUser) {
        throw new Error(
          "Používateľ s týmto emailom nebol nájdený alebo nie je schválený",
        );
      }

      // Skontrolovať, či už pár alebo čakajúca pozvánka neexistuje
      const { data: existingPair } = await supabase
        .from("pairs")
        .select("id")
        .or(
          `and(user_a_id.eq.${user.id},user_b_id.eq.${inviteeUser.id}),and(user_a_id.eq.${inviteeUser.id},user_b_id.eq.${user.id})`,
        )
        .eq("status", "active")
        .limit(1);

      if (existingPair && existingPair.length > 0) {
        throw new Error("Pár s týmto používateľom už existuje");
      }

      const { data: existingInvitation } = await supabase
        .from("pair_invitations")
        .select("id")
        .eq("inviter_id", user.id)
        .eq("invitee_user_id", inviteeUser.id)
        .eq("status", "pending")
        .limit(1);

      if (existingInvitation && existingInvitation.length > 0) {
        throw new Error("Pozvánka tomuto používateľovi už bola odoslaná");
      }

      const { data: invitation, error: invError } = await supabase
        .from("pair_invitations")
        .insert({
          inviter_id: user.id,
          invitee_email: inviteeUser.email,
          invitee_user_id: inviteeUser.id,
          status: "pending",
        })
        .select()
        .single();

      if (invError) throw invError;

      const { data: inviterData } = await supabase
        .from("users")
        .select("display_name, email")
        .eq("id", user.id)
        .single();

      const appUrl =
        import.meta.env.VITE_APP_URL ||
        (import.meta.env.MODE === "production"
          ? "https://app.ayocruz.com"
          : "https://app.ayocruz.com");
      const invitationLink = `${appUrl}/user/pairs?invitation=${invitation.id}`;

      const emailHtml = getPairInvitationTemplate({
        inviteeName:
          inviteeUser.display_name || inviteeUser.email.split("@")[0],
        inviterName:
          inviterData?.display_name || inviterData?.email || "Používateľ",
        invitationLink,
      });

      await emailApi.sendEmail({
        to: inviteeUser.email,
        subject: "Pozvánka na tanečný pár – AC Academy",
        html: emailHtml,
        user_id: user.id,
      });

      await notificationsApi.create({
        user_id: inviteeUser.id,
        type: "pair_invitation",
        title: "Pozvánka na tanečný pár",
        message: `${inviterData?.display_name || inviterData?.email || "Používateľ"} vás pozýva ako tanečného partnera.`,
        data: { invitation_id: invitation.id },
        sendEmail: false,
      });

      return { success: true, data: invitation as PairInvitation };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Nepodarilo sa odoslať pozvánku",
      };
    }
  },

  /**
   * Prijať pozvánku na pár – vytvorí pár a označí pozvánku ako accepted
   */
  async acceptInvitation(
    invitationId: number,
  ): Promise<{ success: boolean; data?: Pair; error?: string }> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: invitation, error: invError } = await supabase
        .from("pair_invitations")
        .select("*")
        .eq("id", invitationId)
        .single();

      if (invError || !invitation) throw new Error("Pozvánka neexistuje");

      if (invitation.invitee_user_id !== user.id) {
        const { data: me } = await supabase
          .from("users")
          .select("email")
          .eq("id", user.id)
          .single();
        if (invitation.invitee_email !== me?.email) {
          throw new Error("Táto pozvánka nie je pre vás");
        }
      }

      if (invitation.status !== "pending") {
        throw new Error("Pozvánka už bola spracovaná");
      }

      const inviteeId =
        invitation.invitee_user_id ||
        (
          await supabase
            .from("users")
            .select("id")
            .eq("email", invitation.invitee_email)
            .single()
        ).data?.id;

      if (!inviteeId) throw new Error("Pozvaný používateľ neexistuje");

      const { data: newPair, error: pairError } = await supabase
        .from("pairs")
        .insert({
          user_a_id: invitation.inviter_id,
          user_b_id: inviteeId,
          status: "active",
        })
        .select()
        .single();

      if (pairError) throw pairError;

      await supabase
        .from("pair_invitations")
        .update({ status: "accepted" })
        .eq("id", invitationId);

      await notificationsApi.create({
        user_id: invitation.inviter_id,
        type: "pair_invitation",
        title: "Pozvánka na pár prijatá",
        message: `Váš pozvaný partner prijal pozvánku. Teraz ste tanečný pár.`,
        data: { pair_id: newPair.id },
        sendEmail: true,
      });

      return { success: true, data: newPair as Pair };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Nepodarilo sa prijať pozvánku",
      };
    }
  },

  /**
   * Odmietnuť pozvánku na pár
   */
  async declineInvitation(
    invitationId: number,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: invitation, error: invError } = await supabase
        .from("pair_invitations")
        .select("*")
        .eq("id", invitationId)
        .single();

      if (invError || !invitation) throw new Error("Pozvánka neexistuje");

      const isInvitee =
        invitation.invitee_user_id === user.id ||
        invitation.invitee_email === user.email;

      if (!isInvitee) throw new Error("Táto pozvánka nie je pre vás");

      if (invitation.status !== "pending") {
        throw new Error("Pozvánka už bola spracovaná");
      }

      await supabase
        .from("pair_invitations")
        .update({ status: "declined" })
        .eq("id", invitationId);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Nepodarilo sa odmietnuť pozvánku",
      };
    }
  },

  /**
   * Získať pozvánky na pár pre aktuálneho používateľa (ako invitee)
   */
  async getMyPairInvitations(): Promise<{
    success: boolean;
    data?: (PairInvitation & {
      inviter?: { display_name: string; email: string };
    })[];
    error?: string;
  }> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const orFilter = user.email
        ? `invitee_user_id.eq.${user.id},invitee_email.eq.${user.email}`
        : `invitee_user_id.eq.${user.id}`;

      const { data: invitations, error } = await supabase
        .from("pair_invitations")
        .select(
          `*,
          inviter:users!inviter_id(display_name, email)`,
        )
        .or(orFilter)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const normalized = (invitations || []).map(
        (
          inv: PairInvitation & {
            inviter?: { display_name: string; email: string } | null;
          },
        ) => {
          const { inviter, ...rest } = inv;
          return {
            ...rest,
            inviter: inviter ?? undefined,
          };
        },
      );

      return {
        success: true,
        data: normalized as (PairInvitation & {
          inviter?: { display_name: string; email: string };
        })[],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Nepodarilo sa načítať pozvánky",
      };
    }
  },

  /**
   * Načítať všetky páry pre admina – zoznam s detailmi používateľov
   * user_b_placeholder sa používa keď user_b_id je null (dočasný pár)
   */
  async listAllForAdmin(): Promise<{
    success: boolean;
    data?: (Pair & {
      user_a?: { id: string; display_name: string | null; email: string };
      user_b?: { id: string; display_name: string | null; email: string };
      user_b_placeholder?: string | null;
    })[];
    error?: string;
  }> {
    try {
      const { data: pairsData, error: pairsError } = await supabase
        .from("pairs")
        .select("*")
        .order("created_at", { ascending: false });

      if (pairsError) throw pairsError;
      const pairs = (pairsData || []) as Pair[];

      if (pairs.length === 0) {
        return { success: true, data: [] };
      }

      const userIds = [
        ...new Set([
          ...pairs.map((p) => p.user_a_id),
          ...pairs
            .map((p) => p.user_b_id)
            .filter((id): id is string => id != null),
        ]),
      ];

      const { data: usersData, error: usersError } = await supabase
        .from("users")
        .select("id, display_name, email")
        .in("id", userIds);

      if (usersError) throw usersError;
      const usersMap = new Map(
        (usersData || []).map(
          (u: { id: string; display_name: string | null; email: string }) => [
            u.id,
            u,
          ],
        ),
      );

      const result = pairs.map((pair) => ({
        ...pair,
        user_a: pair.user_a_id ? usersMap.get(pair.user_a_id) : undefined,
        user_b: pair.user_b_id ? usersMap.get(pair.user_b_id) : undefined,
        user_b_placeholder:
          (pair as Pair & { user_b_placeholder?: string | null })
            .user_b_placeholder ?? null,
      }));

      return { success: true, data: result };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Nepodarilo sa načítať páry",
      };
    }
  },

  /**
   * Breakup a pair (set status to inactive)
   */
  async breakup(pairId: number): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("pairs")
        .update({ status: "inactive" })
        .eq("id", pairId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to breakup pair",
      };
    }
  },
};
